import { getCustomStrikeAlbumId, getScoreResultTargetId, type CustomStrike } from 'models/market';
import type { SportsGame, StructuredTargetVo } from 'models/milestones';

/** Returns the canonical backend artwork, independent of theme. */
export const getMarketRowTargetImageUrl = (target?: { product_details?: unknown }): string | undefined => {
    const productDetails = target?.product_details;
    if (productDetails && typeof productDetails === 'object') {
        const canonicalImageUrl = (productDetails as Record<string, unknown>).image_url;
        if (typeof canonicalImageUrl === 'string' && canonicalImageUrl.trim()) return canonicalImageUrl.trim();
    }
    return undefined;
};

export const isScoreTieMarket = (customStrike: CustomStrike | undefined): boolean => {
    const homeTeamId = customStrike?.home_team_id;
    const awayTeamId = customStrike?.away_team_id;
    const homeScore = customStrike?.home_score !== undefined ? parseInt(customStrike.home_score, 10) : undefined;
    const awayScore = customStrike?.away_score !== undefined ? parseInt(customStrike.away_score, 10) : undefined;
    return (
        !!homeTeamId &&
        !!awayTeamId &&
        homeScore !== undefined &&
        awayScore !== undefined &&
        !isNaN(homeScore) &&
        !isNaN(awayScore) &&
        homeScore === awayScore
    );
};

/** Shared target selection for rows, panel seeds, and independently loaded panels. */
export function getMarketImageTarget(
    customStrike: CustomStrike | undefined,
    targets: Record<string, StructuredTargetVo> | undefined,
): StructuredTargetVo | undefined {
    if (!customStrike || !targets) return undefined;
    // Candidate markets must fall back to their own image when the politician
    // has no artwork (or is still loading), rather than picking the party logo.
    if (customStrike.politician) return targets[customStrike.politician];
    const albumId = getCustomStrikeAlbumId(customStrike);
    const album = albumId ? targets[albumId] : undefined;
    if (getMarketRowTargetImageUrl(album)) return album;
    const scoreId = getScoreResultTargetId(customStrike);
    const score = scoreId ? targets[scoreId] : undefined;
    if (getMarketRowTargetImageUrl(score)) return score;
    if (isScoreTieMarket(customStrike)) return undefined;
    return Object.values(customStrike)
        .map((id) => targets[id])
        .find((target) => !!getMarketRowTargetImageUrl(target));
}

export function resolveMarketImageUrl(
    target: StructuredTargetVo | undefined,
    marketImage: string | null | undefined,
    fallbackImage?: string | null,
): string | undefined {
    const targetImage = getMarketRowTargetImageUrl(target);
    if (targetImage) return targetImage;
    return marketImage ?? fallbackImage ?? undefined;
}

/** Card payloads expose custom-strike targets as `associated_structured_targets`. */
export function getMarketCardImageTarget(
    market: {
        associated_structured_targets?: Record<string, string>;
        structured_target_id?: string;
        structured_target?: StructuredTargetVo;
    },
    targets?: Record<string, StructuredTargetVo>,
): StructuredTargetVo | undefined {
    const politicianId = market.associated_structured_targets?.politician;
    if (politicianId) {
        return (
            getMarketImageTarget({ politician: politicianId }, targets) ??
            (market.structured_target?.id === politicianId ? market.structured_target : undefined)
        );
    }
    return (
        market.structured_target ??
        (market.structured_target_id ? targets?.[market.structured_target_id] : undefined) ??
        getMarketImageTarget(market.associated_structured_targets, targets)
    );
}

/**
 * A target's own artwork, else its team's — for player targets, which don't
 * always carry an image (NFL players ship a numbered jersey; college rosters
 * ship nothing but a `team_id`). `targets` is the hydrated map the team sits
 * in. `undefined` when neither resolves.
 */
export function getTargetImageUrlWithTeamFallback(
    target: StructuredTargetVo | undefined,
    targets: Record<string, StructuredTargetVo> | undefined,
): string | undefined {
    const ownImage = getMarketRowTargetImageUrl(target);
    if (ownImage) return ownImage;
    const teamId = target?.details?.team_id;
    return teamId ? getMarketRowTargetImageUrl(targets?.[teamId]) : undefined;
}

/** Home/away team artwork for a team id, for markets whose player has no artwork of their own. */
export function sportsGameTeamImageUrl(
    sportsGame: Pick<SportsGame, 'home_id' | 'away_id' | 'home_image_url' | 'away_image_url'> | null | undefined,
    teamId: string | undefined,
): string | undefined {
    if (!sportsGame || !teamId) return undefined;
    if (teamId === sportsGame.home_id) return sportsGame.home_image_url;
    if (teamId === sportsGame.away_id) return sportsGame.away_image_url;
    return undefined;
}
